import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// Pages publiques
import Home from "./pages/Home";
import HomeAlt from "./pages/HomeAlt";
import BackgroundSettings from "./pages/admin/BackgroundSettings";
import BlurTool from "./pages/admin/BlurTool";
import BgLibrary from "./pages/admin/BgLibrary";
import PageBackground from "./components/layout/PageBackground";
import Blog from "./pages/Blog";
import Gallery from "./pages/Gallery";
import Shop from "./pages/Shop";
import Membership from "./pages/Membership";
import Founders from "./pages/Founders";
import TeamProfile from "./pages/TeamProfile";
import Donate from "./pages/Donate";
import Contact from "./pages/Contact";
import PaymentV2 from "./pages/PaymentV2";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import MobileApp from "./pages/MobileApp";

// Auth pages
import AuthLanding from "./pages/auth/AuthLanding";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ResetPassword from "./pages/auth/ResetPassword";
import ForgotPassword from "./pages/auth/ForgotPassword";
import Profile from "./pages/Profile";

// Pages légales
import MentionsLegales from "./pages/legal/MentionsLegales";
import PolitiqueConfidentialite from "./pages/legal/PolitiqueConfidentialite";
import CGU from "./pages/legal/CGU";

// Messagerie
import Messages from "./pages/Messages";

// FAQ
import FAQ from "./pages/FAQ";

// Layout
import Navbar from "./components/layout/Navbar";
import RouteTracker from "./components/RouteTracker";
import CookieConsent from "./components/CookieConsent";
import Footer from "./components/layout/Footer";
import AnnouncementBanner from "./components/layout/AnnouncementBanner";
import QuickNavMenu from "./components/layout/QuickNavMenu";
import GlobalBackButton from "./components/layout/GlobalBackButton";

// Protected routes
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Carte from "./pages/Carte";
import ConformiteJuridique from "./pages/dashboard/founder/ConformiteJuridique";
import UsersAdmin from "./pages/dashboard/founder/UsersAdmin";

// Dashboards
import FounderDashboard from "./pages/dashboard/FounderDashboard";
import MemberDashboard from "./pages/dashboard/MemberDashboard";
import GuestDashboard from "./pages/dashboard/GuestDashboard";
import CoFounderDashboard from "./pages/dashboard/CoFounderDashboard";
import TwoFactorAuth from "./pages/dashboard/founder/TwoFactorAuth";
import SystemAdmin from "./pages/dashboard/founder/SystemAdmin";
import AdminConsole from "./pages/dashboard/founder/AdminConsole";
import AdminCenter from "./pages/dashboard/founder/AdminCenter";
import CodeBrowser from "./pages/dashboard/founder/CodeBrowser";
import SecurityMonitor from "./pages/dashboard/founder/SecurityMonitor";

// Admin pages
import MediaManagement from "./pages/admin/MediaManagement";
import PageBuilder from "./pages/admin/PageBuilder";
import SiteSettings from "./pages/admin/SiteSettings";
import NavigationManager from "./pages/admin/NavigationManager";
import PaymentSettings from "./pages/admin/PaymentSettings";
import BentoSettings from "./pages/admin/BentoSettings";
import ContentEditor from "./pages/admin/ContentEditor";
import TeamManager from "./pages/admin/TeamManager";
import AuditLogs from "./pages/admin/AuditLogs";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSystem from "./pages/admin/AdminSystem";
import AuditUserEvents from "./pages/admin/AuditUserEvents";

// Maintenance
import Maintenance from "./pages/Maintenance";
import { fetchPublicMaintenance } from "./services/site";
import AdminMaintenanceBadge from "./components/admin/AdminMaintenanceBadge";

function AppRoutes({ maint, noMaint }) {
  const { user, loading } = useAuth();
  const isFounder = !!user && (user.user_type === 'fondateur' || user.type === 'admin');
  const isGuest = !!user && user.user_type === 'invite';
  // Pendant la maintenance : l'admin (fondateur/co-fondateur) est prioritaire,
  // les testeurs/testrices (membre) et invités peuvent aussi accéder pour tester.
  const canViewDuringMaintenance = isFounder || isGuest
    || (!!user && (user.user_type === 'co-fondateur' || user.user_type === 'membre'));
  const maintenanceActive = !!maint?.enabled && !noMaint;

  if (maintenanceActive && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">Vérification des droits…</div>
    );
  }

  if (maintenanceActive && !canViewDuringMaintenance) {
    return <Maintenance />;
  }

  return (
    <>
      {maintenanceActive && isFounder && <AdminMaintenanceBadge />}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<ProtectedRoute><AppLayout><Home /></AppLayout></ProtectedRoute>} />
        <Route path="/home" element={<ProtectedRoute><AppLayout><Home /></AppLayout></ProtectedRoute>} />
        <Route path="/home-alt" element={<ProtectedRoute><AppLayout><HomeAlt /></AppLayout></ProtectedRoute>} />
        <Route path="/blog" element={<ProtectedRoute><AppLayout><Blog /></AppLayout></ProtectedRoute>} />
        <Route path="/gallery" element={<ProtectedRoute><AppLayout><Gallery /></AppLayout></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><AppLayout><Shop /></AppLayout></ProtectedRoute>} />
        <Route path="/membership" element={<ProtectedRoute><AppLayout><Membership /></AppLayout></ProtectedRoute>} />
        <Route path="/equipe" element={<ProtectedRoute><AppLayout><Founders /></AppLayout></ProtectedRoute>} />
        <Route path="/equipe/:slug" element={<ProtectedRoute><AppLayout><TeamProfile /></AppLayout></ProtectedRoute>} />
        <Route path="/founders" element={<AppLayout><Founders /></AppLayout>} />
        <Route path="/donate" element={<ProtectedRoute><AppLayout><Donate /></AppLayout></ProtectedRoute>} />
        <Route path="/contact" element={<AppLayout><Contact /></AppLayout>} />
        <Route path="/mentions-legales" element={<AppLayout><MentionsLegales /></AppLayout>} />
        <Route path="/confidentialite" element={<AppLayout><PolitiqueConfidentialite /></AppLayout>} />
        <Route path="/cgu" element={<AppLayout><CGU /></AppLayout>} />
        <Route path="/carte" element={<AppLayout><Carte /></AppLayout>} />
        <Route path="/messages" element={<ProtectedRoute><AppLayout><Messages /></AppLayout></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><AppLayout><PaymentV2 /></AppLayout></ProtectedRoute>} />
        <Route path="/payment-v2" element={<ProtectedRoute><AppLayout><PaymentV2 /></AppLayout></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><AppLayout><PaymentSuccess /></AppLayout></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute><AppLayout><PaymentCancel /></AppLayout></ProtectedRoute>} />
        <Route path="/mobile" element={<ProtectedRoute><AppLayout><MobileApp /></AppLayout></ProtectedRoute>} />
        <Route path="/faq" element={<ProtectedRoute><AppLayout><FAQ /></AppLayout></ProtectedRoute>} />

        {/* Auth routes (public) */}
        <Route path="/auth" element={<AppLayout><AuthLanding /></AppLayout>} />
        <Route path="/login" element={<AppLayout><Login /></AppLayout>} />
        <Route path="/register" element={<AppLayout><Register /></AppLayout>} />
        <Route path="/forgot-password" element={<AppLayout><ForgotPassword /></AppLayout>} />
        <Route path="/reset-password" element={<AppLayout><ResetPassword /></AppLayout>} />
        <Route path="/profile" element={<AppLayout><Profile /></AppLayout>} />

        {/* Protected areas (user) */}
        <Route path="/dashboard/fondateur" element={<ProtectedRoute><AppLayout><FounderDashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/membre" element={<ProtectedRoute><AppLayout><MemberDashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/invite" element={<ProtectedRoute><AppLayout><GuestDashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/co-fondateur" element={<ProtectedRoute><AppLayout><CoFounderDashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/2fa" element={<ProtectedRoute><AppLayout><TwoFactorAuth /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/system" element={<ProtectedRoute><AppLayout><SystemAdmin /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/security" element={<ProtectedRoute><AppLayout><SecurityMonitor /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/conformite" element={<ProtectedRoute><AppLayout><ConformiteJuridique /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/utilisateurs" element={<ProtectedRoute><AppLayout><UsersAdmin /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/console" element={<ProtectedRoute><AppLayout><AdminConsole /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/admin" element={<ProtectedRoute><AppLayout><AdminCenter /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/code" element={<ProtectedRoute><AppLayout><CodeBrowser /></AppLayout></ProtectedRoute>} />

        {/* Admin tools */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/media" element={<ProtectedRoute><AppLayout><MediaManagement /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/page-builder" element={<ProtectedRoute><AppLayout><PageBuilder /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/system" element={<ProtectedRoute><AppLayout><AdminSystem /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/site-settings" element={<ProtectedRoute><AppLayout><SiteSettings /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/backgrounds" element={<ProtectedRoute><AppLayout><BackgroundSettings /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/backgrounds/library" element={<ProtectedRoute><AppLayout><BgLibrary /></AppLayout></ProtectedRoute>} />
        <Route path="/dashboard/fondateur/floutage" element={<ProtectedRoute><AppLayout><BlurTool /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/audit/users" element={<ProtectedRoute><AppLayout><AuditUserEvents /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/navigation" element={<ProtectedRoute><AppLayout><NavigationManager /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/payment-settings" element={<ProtectedRoute><AppLayout><PaymentSettings /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/bento" element={<ProtectedRoute><AppLayout><BentoSettings /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/editor" element={<ProtectedRoute><AppLayout><ContentEditor /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/team" element={<ProtectedRoute><AppLayout><TeamManager /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/audit-logs" element={<ProtectedRoute><AppLayout><AuditLogs /></AppLayout></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageBackground />
      <div className="relative z-10">
        <AnnouncementBanner />
        <Navbar />
        <RouteTracker />
        <CookieConsent />
        <GlobalBackButton />
        <main className="min-h-[70vh]">{children}</main>
        <Footer />
        <QuickNavMenu />
      </div>
    </div>
  );
}

function App() {
  const [maint, setMaint] = React.useState({ enabled: false, message: "" });
  const [maintLoading, setMaintLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchPublicMaintenance();
        setMaint(res || { enabled: false, message: "" });
      } catch (e) {
        setMaint({ enabled: false, message: "" });
      } finally {
        setMaintLoading(false);
      }
    };
    load();

    // Filet de sécurité: si la vérification de maintenance se bloque, on affiche
    // quand même le site au bout de 10s (mode maintenance considéré désactivé).
    const safety = setTimeout(() => setMaintLoading(false), 10000);
    const id = setInterval(load, 60000);
    return () => { clearInterval(id); clearTimeout(safety); };
  }, []);

  const noMaint = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.has('no_maint') || sp.get('no_maint') === '1';
    } catch (e) { return false; }
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            {maintLoading ? (
              <div className="p-8 text-center text-muted-foreground">Chargement…</div>
            ) : (
              <AppRoutes maint={maint} noMaint={noMaint} />
            )}
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;